/*
Capture flow orchestrator — sheet-driven (the user has already tapped a
specific arbre on the map, so we know its identity + coordinates).

iOS Safari constraint: an `<input type=file>` click must originate from
the same synchronous call stack as a user gesture. After even one
`await`, iOS silently suppresses the click. So we **fire the camera
first** (synchronously, inside the user-gesture-bearing handler) and
then run GPS + IDB checks in parallel.

Sequence:
  1. capturePhoto()  -- input.click() fires synchronously, camera opens.
  2. requestFreshFix() in parallel  (cache hit usually returns instantly).
  3. await both.
  4. Distance check user fix vs. target arbre coords (≤ 35 m).
  5. Persist photo Blob + capture row.

Status updates are emitted to the caller via inOnStatus so the UI can
narrate phase transitions. The flow never throws — errors become a
final 'error' status, the UI keeps showing the message and the FAB
release lives in a `try/finally` at the call site.
*/

import { requestFreshFix, haversineMeters, type FreshFix } from './geolocation'
import { capturePhoto, type CapturedPhoto } from './camera'
import { reencodeForStorage } from './imagePipeline'
import { savePhoto } from '../data/photoStore'
import { insertCapture, captureExists, listCaptures } from '../data/captureRepository'
import { seasonFromTimestamp } from '../data/season'
import { speciesNameFor, displayName } from '../data/speciesIndex'

export const kMaxArbreMeters: number = 30  // proximity gate — at or below this, you're definitely "at" the tree
// Hysteresis band applied symmetrically by the button state machine
// (MapScreen.updateCaptureButtonState) and the actual capture gate
// below: the button enters "ready" at ≤ kMaxArbreMeters and only
// flips back to "far" above kMaxArbreMeters + kHysteresisMeters.
// Without this, a 28→32→29 m GPS jitter cycle makes the button
// flicker disabled mid-tap.
export const kHysteresisMeters: number = 5
export const kCaptureCutoffMeters: number = kMaxArbreMeters + kHysteresisMeters  // hard limit applied at the moment of capture, kept aligned with the button's hysteresis ceiling

export type CapturePhase =
	| 'camera'
	| 'encoding'  // photo validée (overlay caméra fermé), encodage JPEG en cours — l'UI couvre la transition d'un splash
	| 'gps'
	| 'saving'
	| 'success'
	| 'cancelled'
	| 'error'

export interface TargetArbre
{
	arbreId: number
	speciesIndex: number
	remarquable: boolean
	lat: number
	lon: number
}

export interface CaptureStatus
{
	phase: CapturePhase
	message: string | null
	captureId: number | null
	target: TargetArbre | null
	speciesLabel: string | null
	firstForSpecies: boolean
	firstCaptureEver: boolean  // true only on the very first capture inserted in this profile — drives the persistent-storage banner
}

/*
Run the capture pipeline against a sheet-selected arbre. CALL THIS
SYNCHRONOUSLY FROM THE BUTTON CLICK HANDLER — capturePhoto() needs the
user-gesture context to actually open the iOS camera.
*/
export async function runCaptureFlow(
	inTarget: TargetArbre,
	inOnStatus: (inStatus: CaptureStatus) => void,
): Promise<void>
{
	const vLabel = labelFor(inTarget.speciesIndex)  // friendly French name for status messages

	// Step 1 — fire the camera SYNCHRONOUSLY. The Promise constructor's
	// executor body runs synchronously, so the getUserMedia call inside
	// `capturePhoto()` reaches the browser within the user gesture.
	emit(inOnStatus, 'camera', 'Ouverture de la caméra…', inTarget, vLabel)
	// The onCommitted hook fires synchronously at the validation tap (camera
	// overlay already gone) so the UI can mount the capture splash without a
	// visible frame of bare map between camera and outcome.
	const vPhotoP = capturePhoto(() => emit(inOnStatus, 'encoding', null, inTarget, vLabel))  // do NOT await yet — keep the next ops in parallel

	// Step 2 — kick off GPS in parallel. The user is choosing a photo
	// while we resolve their position.
	const vGpsP = requestFreshFix().catch((inErr: unknown) => inErr)  // never throws; we inspect the result type below

	// Step 3 — wait for the photo. If the user cancels, abort cleanly.
	let vPhoto: CapturedPhoto
	try
	{
		vPhoto = await vPhotoP
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Capture annulée'
		const vIsCancel = vMsg === 'Capture cancelled' || vMsg === 'No photo selected'
		emit(inOnStatus, vIsCancel ? 'cancelled' : 'error', vIsCancel ? null : vMsg, inTarget, vLabel)
		return
	}

	// Step 4 — now resolve GPS. Photo's already in hand; if GPS fails we
	// have to discard the photo and surface the error (no save without
	// a verified position).
	emit(inOnStatus, 'gps', 'Vérification de la position…', inTarget, vLabel)
	const vGpsResult = await vGpsP
	if (vGpsResult instanceof Error)
	{
		emit(inOnStatus, 'error', gpsErrorMessage(vGpsResult), inTarget, vLabel)
		return
	}
	const vFix = vGpsResult as FreshFix

	// Step 5 — proximity check. We tapped the tree; we still need to be
	// physically near it (capture-on-proximity loop). Distance via haversine since
	// 35 m at Paris's latitude is well below any cartographic distortion.
	// We apply the same hysteresis ceiling as the button state machine
	// (`kCaptureCutoffMeters`) so a "ready"-looking button can't be
	// rejected by a stricter gate underneath it.
	const vDist = haversineMeters(vFix.lat, vFix.lon, inTarget.lat, inTarget.lon)
	if (vDist > kCaptureCutoffMeters)
	{
		emit(inOnStatus, 'error',
			`Tu es à ${vDist.toFixed(0)} m de cet arbre. Approche-toi (< ${kCaptureCutoffMeters} m).`,
			inTarget, vLabel)
		return
	}

	// Step 6 — persist.
	emit(inOnStatus, 'saving', 'Enregistrement…', inTarget, vLabel)

	if (await captureExists(inTarget.arbreId, vPhoto.takenAt))
	{
		emit(inOnStatus, 'error', 'Cette capture existe déjà.', inTarget, vLabel)
		return
	}

	// "First time we see this species" check — read BEFORE inserting.
	// `vFirstCaptureEver` mirrors the same pre-insert read: when the
	// captures list is empty, this is the inaugural capture of the
	// profile, which the UI uses to one-shot the persistent-storage
	// request + banner.
	const vAll = await listCaptures()
	const vFirstForSpecies = !vAll.some((inC) => inC.speciesIndex === inTarget.speciesIndex)
	const vFirstCaptureEver = vAll.length === 0

	try
	{
		// Re-encode before persisting: drops EXIF (GPS, device serials,
		// software fingerprint) by design — canvas exports start from
		// blank metadata — and shrinks the blob to ~300-700 KB so the
		// IDB photo store doesn't fill the origin's quota in a few dozen
		// captures. Mirrors Android `compressCapture`. Errors here are
		// soft: we surface a friendly status and bail before INSERT so
		// the IDB never holds a row pointing at a missing or corrupt
		// photo.
		let vEncoded: Blob
		try
		{
			vEncoded = await reencodeForStorage(vPhoto.blob)
		}
		catch (vEncErr)
		{
			const vEncMsg = vEncErr instanceof Error ? vEncErr.message : 'inconnue'
			emit(inOnStatus, 'error', `Erreur de traitement photo (${vEncMsg}).`, inTarget, vLabel)
			return
		}
		// Drop the raw File reference now so the GC can reclaim the
		// 5-10 MB iPhone JPEG buffer before savePhoto's IDB transaction
		// allocates the encoded blob — only filename and takenAt are
		// read after this point. Local cast keeps the public type
		// strict; the mutation never escapes this scope.
		;(vPhoto as { blob: Blob | null }).blob = null
		await savePhoto({ filename: vPhoto.filename, blob: vEncoded, takenAt: vPhoto.takenAt })

		const vCaptureId = await insertCapture({
			arbreId: inTarget.arbreId,
			timestamp: vPhoto.takenAt,
			speciesIndex: inTarget.speciesIndex,
			season: seasonFromTimestamp(vPhoto.takenAt),
			photoFilename: vPhoto.filename,
			lat: vFix.lat,
			lon: vFix.lon,
		})

		const vSuccess: CaptureStatus = {
			phase: 'success',
			message: null,
			captureId: vCaptureId,
			target: inTarget,
			speciesLabel: vLabel,
			firstForSpecies: vFirstForSpecies,
			firstCaptureEver: vFirstCaptureEver,
		}
		inOnStatus(vSuccess)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : "Erreur d'enregistrement"
		emit(inOnStatus, 'error', vMsg, inTarget, vLabel)
	}
}

function emit(
	inHandler: (inStatus: CaptureStatus) => void,
	inPhase: CapturePhase,
	inMessage: string | null,
	inTarget: TargetArbre | null,
	inLabel: string | null,
): void
{
	inHandler({
		phase: inPhase,
		message: inMessage,
		captureId: null,
		target: inTarget,
		speciesLabel: inLabel,
		firstForSpecies: false,
		firstCaptureEver: false,
	})
}

function labelFor(inSpeciesIndex: number): string
{
	const vName = speciesNameFor(inSpeciesIndex)
	if (!vName) return 'Espèce inconnue'
	// Cascade nv → nc → latin (parity Android displayNomCommun). The
	// capture-success toast surface picks up the unique vernacular
	// "Marronnier commun" instead of the broad legacy "Marronnier".
	return displayName(vName)
}

function gpsErrorMessage(inErr: unknown): string
{
	const vRaw = inErr instanceof Error ? inErr.message : ''  // raw message from helper
	if (vRaw.startsWith('Fix too coarse')) return 'GPS trop imprécis. Essaye à découvert.'
	if (vRaw.startsWith('Fix too stale')) return 'Position GPS trop ancienne. Réessaye.'
	if (vRaw.includes('not available')) return 'GPS indisponible sur ce navigateur.'
	if (vRaw.toLowerCase().includes('denied')) return 'Permission GPS refusée.'
	return 'Position GPS introuvable.'
}

